import { useState, ReactNode } from "react"

import { motion } from "framer-motion"
import { X } from "lucide-react"

import { ActionTooltipButton } from "@/components/buttons/ActionTooltipButton"
import { DocumentModal } from "@/components/modals/DocumentModal"
import { DocumentPanel } from "@/components/panels/DocumentPanel"
import { cn } from "@/lib/utils"

interface DocumentOverlayProps {
    children: ReactNode
    document: string
    removeFromCollection?: (document: string) => void
    className?: string
    modalMode?: boolean
}
export function DocumentOverlay({ children, document, removeFromCollection, className, modalMode = false }: Readonly<DocumentOverlayProps>) {
    const [isOpen, setIsOpen] = useState(false)

    const handleClick = () => {
        setIsOpen(true)
    }

    const handleRemoveFromCollection = () => {
        if (removeFromCollection) {
            removeFromCollection(document)
            setIsOpen(false)
        }
    }

    if (modalMode) {
        return (
            <DocumentModal document={document} open={isOpen} setOpen={setIsOpen} removeFromCollection={removeFromCollection} className={className}>
                {children}
            </DocumentModal>
        )
    }

    return (
        <div className={cn("w-full flex items-center justify-center", className)}>
            <div onClick={handleClick} className="w-full align-left">
                {children}
            </div>
            {
                isOpen && (
                    <div className="z-65 fixed w-full inset-0 bg-opacity-50 flex justify-end top-0">
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-80 h-full w-full bg-white shadow-lg p-0 relative"
                        >
                            <DocumentPanel id={document} removeFromCollection={removeFromCollection ? handleRemoveFromCollection : undefined}>
                                <ActionTooltipButton icon={<X />} variant="outline" tooltip="Close Document Overlay" onClick={() => setIsOpen(false)} />
                            </DocumentPanel>
                        </motion.div>
                    </div>
                )
            }
        </div>
    )
}